"use client";

import type { ReactNode } from "react";

import "@/app/authenticated-marketing.css";

import { MarketingPageShell } from "@/components/marketing/MarketingPageShell";
import { AppSidebarLayout } from "@/components/shell/AppSidebarLayout";
import { usePlatformAuth } from "@/hooks/use-platform-auth";
import { cn } from "@/lib/utils";

export type AppAwareMarketingShellClientProps = {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  footerClassName?: string;
};

/** Neutral shell while auth is unknown (no public marketing navbar). */
function MarketingChromePending({
  children,
  className,
  contentClassName,
}: Pick<
  AppAwareMarketingShellClientProps,
  "children" | "className" | "contentClassName"
>) {
  return (
    <div
      className={cn(
        "marketing-linear-page min-h-[100svh] bg-[#050505] text-[var(--color-text-primary)]",
        className,
      )}
    >
      <main
        className={cn("pt-[var(--marketing-subpage-main-top)]", contentClassName)}
      >
        {children}
      </main>
    </div>
  );
}

/**
 * Guests: marketing navbar + footer.
 * Signed-in: workspace sidebar shell only (Explore nav in sidebar).
 */
export function AppAwareMarketingShellClient({
  children,
  className,
  contentClassName,
  footerClassName,
}: AppAwareMarketingShellClientProps) {
  const { isAuthenticated, isLoading, isResolved } = usePlatformAuth();

  if (isLoading && !isResolved) {
    return (
      <MarketingChromePending
        className={className}
        contentClassName={contentClassName}
      >
        {children}
      </MarketingChromePending>
    );
  }

  if (isAuthenticated) {
    return (
      <AppSidebarLayout>
        <div
          className={cn(
            "inora-auth-marketing marketing-linear-page w-full bg-[#050505] text-[var(--color-text-primary)]",
            className,
          )}
        >
          <main
            className={cn(
              "w-full pt-6 md:pt-10",
              contentClassName,
            )}
          >
            {children}
          </main>
        </div>
      </AppSidebarLayout>
    );
  }

  return (
    <MarketingPageShell
      className={className}
      contentClassName={contentClassName}
      footerClassName={footerClassName}
    >
      {children}
    </MarketingPageShell>
  );
}
